<template>
  <aside
    class="mapv3-panel mapv3-panel--info"
    aria-label="Szczegóły pojazdu"
  >
    <!-- ===== Header ===== -->
    <div class="mapv3-panel__header">
      <div class="mapv3-panel__header-top">
        <div class="mapv3-panel__title-wrap">
          <div class="mapv3-panel__plate">
            {{ vehicle._plate || vehicle.registration || vehicle.imei }}
          </div>
          <div class="mapv3-panel__model">
            {{ vehicle.model || vehicle.name || '—' }}
          </div>
        </div>
        <div class="mapv3-panel__header-actions">
          <span
            class="mapv3-panel__status-badge"
            :style="{ background: statusPalette.tint, color: statusPalette.color }"
          >
            {{ statusLabel }}
          </span>
          <button
            class="mapv3-panel-close"
            type="button"
            :title="$t('components.map.list.collapse')"
            @click="$emit('close')"
          >
            <i class="bi bi-x-lg" />
          </button>
        </div>
      </div>
      <div
        v-if="hasTacho"
        class="mapv3-panel__driver"
      >
        <div
          class="mapv3-panel__driver-avatar"
          :style="{ background: statusPalette.color }"
        >
          {{ driverInitials }}
        </div>
        <div class="mapv3-panel__driver-main">
          <div class="mapv3-panel__driver-name">
            {{ driverName }}
          </div>
          <div class="mapv3-panel__driver-meta">
            {{ currentCard ? ('Karta ' + currentCard) : $t('components.map.panel.noCard') }}
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Tabs ===== -->
    <div
      class="mapv3-panel__tabs"
      role="tablist"
    >
      <button
        v-for="tab in tabs"
        :key="tab"
        class="mapv3-panel__tab"
        :class="{ 'mapv3-panel__tab--active': activeTab === tab }"
        type="button"
        role="tab"
        @click="activeTab = tab"
      >
        {{ $t('components.map.panel.tabs.' + tab) }}
      </button>
    </div>

    <div class="mapv3-panel__content">
      <!-- ----- TACHO ----- -->
      <div
        v-show="activeTab === 'tacho'"
        class="mapv3-panel__tab-pane"
      >
        <div class="mapv3-tacho__progress-card">
          <div class="mapv3-tacho__progress-head">
            <span class="mapv3-tacho__progress-label">{{ $t('components.map.panel.tacho.continuous') }}</span>
            <span class="mapv3-tacho__progress-value">{{ continuous.text }}</span>
          </div>
          <div class="mapv3-tacho__bar">
            <div
              class="mapv3-tacho__bar-fill"
              :style="{ width: continuous.pct + '%', background: continuous.color }"
            />
          </div>
          <div class="mapv3-tacho__caption">
            {{ $t('components.map.panel.tacho.remaining', { v: continuous.remaining }) }}
          </div>
        </div>

        <div
          v-if="loadingTacho"
          class="mapv3-tacho__state"
        >
          {{ $t('components.map.panel.loading') }}
        </div>
        <div
          v-else-if="!contract"
          class="mapv3-tacho__state"
        >
          {{ currentCard ? $t('components.map.panel.tacho.noDriver') : $t('components.map.panel.tacho.pick') }}
        </div>

        <template
          v-for="section in tachoSections"
          v-else
          :key="section.title"
        >
          <div class="mapv3-section-title">
            {{ section.title }}
          </div>
          <div
            v-for="row in section.rows"
            :key="row.label"
            class="mapv3-row"
          >
            <span class="mapv3-row__label">{{ row.label }}<span
              v-if="row.source && row.source !== 'none'"
              class="mapv3-tacho__source"
              :class="'mapv3-tacho__source--' + row.source"
              :title="sourceTitle(row.source)"
            >{{ sourceLabel(row.source) }}</span></span>
            <span
              class="mapv3-row__value"
              :class="{ 'mapv3-row__value--wrap': row.wrap }"
            >{{ row.value }}</span>
          </div>
        </template>
      </div>

      <!-- ----- CAN ----- -->
      <div
        v-show="activeTab === 'can'"
        class="mapv3-panel__tab-pane"
      >
        <div
          v-if="!canCards.length"
          class="mapv3-tacho__state"
        >
          {{ $t('components.map.panel.can.empty') }}
        </div>
        <div
          v-if="canCards.length"
          class="mapv3-can__grid"
        >
          <div
            v-for="card in canCards"
            :key="card.key"
            class="mapv3-can__tile"
            :class="{ 'mapv3-can__tile--wide': card.bar }"
          >
            <div class="mapv3-can__tile-head">
              <i :class="'bi ' + card.icon" />
              <span>{{ card.label }}</span>
            </div>
            <div class="mapv3-can__tile-value">
              {{ card.value }}<span
                v-if="card.unit"
                class="mapv3-can__tile-unit"
              > {{ card.unit }}</span><span
                v-if="card.sub"
                class="mapv3-can__tile-sub"
              > · {{ card.sub }}</span>
            </div>
            <div
              v-if="card.bar"
              class="mapv3-tacho__bar"
            >
              <div
                class="mapv3-tacho__bar-fill"
                :style="{ width: card.barPct + '%', background: card.barColor || statusPalette.color }"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- ----- TRASA ----- -->
      <div
        v-show="activeTab === 'trasa'"
        class="mapv3-panel__tab-pane"
      >
        <div class="mapv3-trasa__ranges">
          <button
            v-for="r in ['day', 'week', 'custom']"
            :key="r"
            class="mapv3-trasa__range"
            :class="{ 'mapv3-trasa__range--active': trackRange === r }"
            type="button"
            @click="setRange(r)"
          >
            {{ $t('components.map.panel.trasa.ranges.' + r) }}
          </button>
        </div>

        <div
          v-if="showCustomPicker"
          class="mapv3-trasa__picker"
        >
          <div class="mapv3-trasa__picker-head">
            {{ $t('components.map.panel.trasa.customTitle') }}
          </div>
          <div class="mapv3-trasa__picker-grid">
            <label class="mapv3-trasa__field">
              <span>{{ $t('components.map.panel.trasa.from') }}</span>
              <input
                v-model="customFrom"
                type="datetime-local"
                step="60"
              >
            </label>
            <label class="mapv3-trasa__field">
              <span>{{ $t('components.map.panel.trasa.to') }}</span>
              <input
                v-model="customTo"
                type="datetime-local"
                step="60"
              >
            </label>
          </div>
          <div
            v-if="rangeError"
            class="mapv3-trasa__error"
          >
            {{ rangeError }}
          </div>
          <div class="mapv3-trasa__picker-actions">
            <button
              class="mapv3-trasa__btn mapv3-trasa__btn--ghost"
              type="button"
              @click="cancelCustom"
            >
              {{ $t('components.map.panel.trasa.cancel') }}
            </button>
            <button
              class="mapv3-trasa__btn mapv3-trasa__btn--primary"
              type="button"
              @click="applyCustomRange"
            >
              {{ $t('components.map.panel.trasa.apply') }}
            </button>
          </div>
        </div>

        <div
          v-if="loadingRoute"
          class="mapv3-trasa__empty"
        >
          {{ $t('components.map.panel.loading') }}
        </div>
        <template v-else>
          <div class="mapv3-section-title">
            {{ $t('components.map.panel.trasa.summary') }}
          </div>
          <div
            v-for="row in trasaRows"
            :key="row.label"
            class="mapv3-row"
          >
            <span class="mapv3-row__label">{{ row.label }}</span>
            <span class="mapv3-row__value">{{ row.value }}</span>
          </div>

          <div class="mapv3-section-title">
            {{ $t('components.map.panel.trasa.events') }}
          </div>
          <div
            v-if="!routeEvents.length"
            class="mapv3-trasa__empty"
          >
            {{ $t('components.map.panel.trasa.eventsEmpty') }}
          </div>
          <div
            v-for="(ev, i) in routeEvents"
            :key="i"
            class="mapv3-trasa__event"
          >
            <span class="mapv3-trasa__event-icon"><i class="bi bi-p-square" /></span>
            <div class="mapv3-trasa__event-main">
              <div class="mapv3-trasa__event-title">
                {{ $t('components.map.panel.trasa.eventStop') }} · {{ ev.duration }}
              </div>
              <div class="mapv3-trasa__event-time">
                {{ ev.time }}
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </aside>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import dayjs from 'dayjs'
import { STATUS_PALETTE, VehicleStatus } from '@/services/map/vehicle-status'
import { detectStops } from '@/services/map/route-events'
import { hoursMinutes, groupThousands, formatDateTime, formatDateFull } from '@/utils/tacho/format'
import {
  getVehicleRoute,
  getHistorySummary,
  getTachoActivityDrivers,
  getTachoActivityDriver
} from '@/crud/getters'

// Placeholder card numbers written by devices when no card is inserted.
const CARD_PLACEHOLDERS = new Set(['FFFFFFFFFFFFFFFF', '0000000000000000', 'FFFF', '0', ''])

// card_number -> { id, name }, fetched once per session from the stored driver-activity grid.
let cardDriverMapPromise: Promise<Record<string, { id: number; name: string }>> | null = null

function loadCardDriverMap(): Promise<Record<string, { id: number; name: string }>> {
  if (!cardDriverMapPromise) {
    cardDriverMapPromise = getTachoActivityDrivers(1000)
      .then((res: any) => {
        const rows = res?.data?.data ?? res?.data ?? []
        const map: Record<string, { id: number; name: string }> = {}
        ;(Array.isArray(rows) ? rows : []).forEach((row: any) => {
          const card = row?.card_number
          const id = Number(row?.driver_id)
          if (!card || !Number.isFinite(id)) return
          const name = row?.holder_name
            || [row?.name, row?.surname].filter(Boolean).join(' ').trim()
            || [row?.first_name, row?.last_name].filter(Boolean).join(' ').trim()
          map[String(card)] = { id, name: name || '' }
        })
        return map
      })
      .catch(() => ({}))
  }
  return cardDriverMapPromise
}

export default defineComponent({
  name: 'Mapv3InfoPanel',
  props: {
    vehicle: { type: Object, required: true }
  },
  emits: ['close'],
  data() {
    return {
      activeTab: 'tacho' as 'tacho' | 'can' | 'trasa',
      trackRange: 'day' as 'day' | 'week' | 'custom',
      customFrom: '',
      customTo: '',
      rangeError: '',
      showCustomPicker: false,
      loadingTacho: false,
      loadingRoute: false,
      contract: null as any,
      driverName: '—',
      currentCard: null as string | null,
      summary: null as any,
      routePoints: [] as Array<any>
    }
  },
  computed: {
    // Whether the vehicle is equipped with a tachograph (device.has_tacho). Without it there is
    // no driver/tacho data, so the Tacho tab and the driver header are hidden.
    hasTacho(): boolean {
      const raw = (this.vehicle as any)?.has_tacho
      return raw === true || raw === 1 || raw === '1'
    },
    tabs(): Array<'tacho' | 'can' | 'trasa'> {
      // Tacho only for tacho-equipped vehicles; CAN only when it actually has data (voltage or
      // CAN metrics) — no empty/misleading tabs. Trasa always applies (GPS route).
      const list: Array<'tacho' | 'can' | 'trasa'> = []
      if (this.hasTacho) list.push('tacho')
      if (this.canCards.length > 0) list.push('can')
      list.push('trasa')
      return list
    },
    statusPalette(): { color: string; tint: string } {
      const status: VehicleStatus = (this.vehicle as any)._status || 'rest'
      return STATUS_PALETTE[status] || STATUS_PALETTE.rest
    },
    statusLabel(): string {
      return this.$t('components.map.list.status.' + ((this.vehicle as any)._status || 'rest')) as string
    },
    driverInitials(): string {
      const name = (this.driverName || '').trim()
      if (!name || name === '—') return '–'
      const parts = name.split(/\s+/).filter(Boolean)
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },
    continuous(): { text: string; pct: number; remaining: string; color: string } {
      const c = this.contract?.times?.continuous
      const limit = c?.limit_min ?? 270
      const driving = c?.driving_min ?? 0
      const pct = limit > 0 ? Math.min(100, Math.round((driving / limit) * 100)) : 0
      return {
        text: this.hm(driving) + ' / ' + this.hm(limit),
        pct,
        remaining: this.hm(c?.remaining_min ?? limit),
        color: pct >= 100 ? STATUS_PALETTE.alert.color : STATUS_PALETTE.drive.color
      }
    },
    tachoSections(): Array<{ title: string; rows: Array<{ label: string; value: string; wrap?: boolean; source?: string }> }> {
      const t = this.contract?.times
      if (!t) return []
      const stored = this.contract?.stored || {}
      const source = stored.last_filename
        ? this.$t('components.map.panel.tacho.sourceDdd') as string
        : this.$t('components.map.panel.tacho.sourceEstimate') as string
      const coverage = stored.last_uploaded_at
        ? formatDateFull(stored.last_uploaded_at, true)
        : (stored.range_from && stored.range_to ? stored.range_from + ' – ' + stored.range_to : '—')
      const L = (k: string) => this.$t('components.map.panel.tacho.' + k) as string
      const src = t.sources || {}
      return [
        {
          title: L('sectionCalc'),
          rows: [
            { label: L('calcSource'), value: source, wrap: true },
            { label: L('calcCoverage'), value: coverage, wrap: true }
          ]
        },
        {
          title: L('sectionDriving'),
          rows: [
            { label: L('day'), value: this.hm(t.day?.driving_min), source: src.day },
            { label: L('twoWeeks'), value: this.hm(t.weeks?.two_weeks_driving_min), source: src.two_weeks },
            { label: L('long'), value: this.cycleRatio(t.cycle?.extended_daily_drives, 2), source: src.cycle },
            { label: L('workdays'), value: this.cycleRatio(t.cycle?.workdays, 6), source: src.cycle },
            { label: L('weekCurrent'), value: this.hm(t.weeks?.current_driving_min), source: src.current_week },
            { label: L('weekPrev'), value: this.hm(t.weeks?.previous_driving_min), source: src.previous_week },
            { label: L('weekTwoAgo'), value: this.hm(t.weeks?.two_weeks_ago_driving_min), source: src.two_weeks_ago },
            { label: L('twoWeeksPrev'), value: this.hm(t.weeks?.previous_two_weeks_driving_min), source: src.previous_two_weeks }
          ]
        },
        {
          title: L('sectionWork'),
          rows: [
            { label: L('workDay'), value: this.hm(t.day?.work_min), source: src.day },
            { label: L('workWeek'), value: this.hm(t.weeks?.current_work_min), source: src.current_week }
          ]
        },
        {
          title: L('sectionRest'),
          rows: [
            { label: L('restCurrent'), value: this.restDuration(t.last_daily_rest), source: src.daily_rest },
            { label: L('restDaily'), value: this.restSpan(t.last_daily_rest), wrap: true, source: src.daily_rest },
            { label: L('restDailyNote'), value: this.restKind(t.last_daily_rest), wrap: true, source: src.daily_rest },
            { label: L('restWeekly'), value: this.restDuration(t.last_weekly_rest), source: src.weekly_rest },
            { label: L('restLastWeeklyType'), value: this.restKind(t.last_weekly_rest), wrap: true, source: src.weekly_rest },
            { label: L('restLastWeeklyHint'), value: this.restSpan(t.last_weekly_rest), wrap: true, source: src.weekly_rest },
            { label: L('restShort'), value: this.cycleRatio(t.cycle?.short_daily_rests, 3), source: src.cycle }
          ]
        }
      ]
    },
    canCards(): Array<any> {
      const v: any = this.vehicle
      const cards: Array<any> = []
      const temp = this.num(v.can_coolant_temp)
      if (temp !== null) cards.push({ key: 'temp', icon: 'bi-thermometer-half', label: this.$t('components.map.panel.can.temp'), value: temp, unit: '°C' })
      const rpm = this.num(v.can_rpm)
      if (rpm !== null) cards.push({ key: 'rpm', icon: 'bi-speedometer', label: this.$t('components.map.panel.can.rpm'), value: groupThousands(rpm), unit: 'rpm' })
      const odo = this.num(v.can_odometer)
      if (odo !== null) cards.push({ key: 'odo', icon: 'bi-truck', label: this.$t('components.map.panel.can.odometer'), value: groupThousands(Math.round(odo)), unit: 'km' })
      // Fuel: prefer litres (backend derives them from % × tank capacity); fall back to % when no
      // tank is configured. Treat 0 litres as "no litres" so it doesn't print "0 L" next to a
      // non-zero bar, and hide the card entirely when there is no fuel reading at all.
      const litersRaw = this.num(v.can_fuel_level_liters)
      const liters = litersRaw !== null && litersRaw > 0 ? litersRaw : null
      const pct = this.num(v.can_fuel_level)
      if (liters !== null || (pct !== null && pct > 0)) {
        const fuelCard: any = {
          key: 'fuel',
          icon: 'bi-fuel-pump',
          label: this.$t('components.map.panel.can.fuel'),
          value: liters !== null ? liters : pct,
          unit: liters !== null ? 'L' : '%'
        }
        if (pct !== null) {
          fuelCard.bar = true
          fuelCard.barPct = Math.max(0, Math.min(100, pct))
          // Bar fill by level: >50 % green, 30–50 % warning, <30 % red.
          fuelCard.barColor = pct >= 50 ? '#16a34a' : (pct >= 30 ? '#f59e0b' : '#dc2626')
          // When litres are shown, add the percentage alongside for context.
          if (liters !== null) fuelCard.sub = Math.round(pct) + '%'
        }
        cards.push(fuelCard)
      }
      const volt = this.num(v.voltage)
      if (volt !== null) cards.push({ key: 'voltage', icon: 'bi-battery-full', label: this.$t('components.map.panel.can.voltage'), value: (volt / 1000).toFixed(2), unit: 'V' })
      // Wide tiles (the fuel bar) go last so the compact metrics fill the 2-column grid in full
      // rows (e.g. temperature|rpm, odometer|voltage) before the full-width fuel tile.
      return [...cards.filter((c) => !c.bar), ...cards.filter((c) => c.bar)]
    },
    trasaRows(): Array<{ label: string; value: string }> {
      const s = this.summary || {}
      const L = (k: string) => this.$t('components.map.panel.trasa.' + k) as string
      const rows: Array<{ label: string; value: string }> = [
        { label: L('points'), value: String(this.routePoints.length || 0) },
        { label: L('distanceMap'), value: this.km(s.map_distance) },
        { label: L('duration'), value: this.routeDuration() }
      ]
      // CAN-only metrics: shown only when the device actually reports them (many devices have no
      // CAN / no cumulative fuel counter, so 0 L / — would just be noise).
      const canDist = Number(s.can_distance)
      if (Number.isFinite(canDist) && canDist > 0) {
        rows.push({ label: L('distanceCan'), value: this.km(s.can_distance) })
      }
      const fuelUsed = Number(s.fuel_used)
      if (Number.isFinite(fuelUsed) && fuelUsed > 0) {
        rows.push({ label: L('fuelUsed'), value: fuelUsed.toFixed(1) + ' L' })
        const avg = this.avgConsumption(s)
        if (avg !== '—') rows.push({ label: L('avg'), value: avg })
      }
      rows.push({ label: L('range'), value: this.rangeLabel() })
      return rows
    },
    // Stop events (postoje) — shared stop detection with the map's stop markers, newest first.
    routeEvents(): Array<{ time: string; duration: string }> {
      return detectStops(this.routePoints)
        .reverse()
        .slice(0, 8)
        .map((e) => ({
          time: dayjs.unix(e.startTs).format('DD.MM HH:mm'),
          duration: hoursMinutes(Math.round((e.endTs - e.startTs) / 60))
        }))
    }
  },
  watch: {
    vehicle: {
      immediate: true,
      handler() {
        this.contract = null
        this.driverName = (this.vehicle as any)?._driver || '—'
        this.currentCard = null
        this.summary = null
        this.routePoints = []
        this.trackRange = 'day'
        // Land on the most relevant tab for this vehicle: Tacho when equipped, else the route.
        this.activeTab = this.hasTacho ? 'tacho' : 'trasa'
        this.loadRoute(true)
      }
    }
  },
  methods: {
    hm(min: any): string {
      const n = Number(min)
      return Number.isFinite(n) ? hoursMinutes(n) : '—'
    },
    count(n: any): string {
      const v = Number(n)
      return Number.isFinite(v) ? String(v) : '—'
    },
    // Cycle counters shown as "used / limit" (561/2006 allowances) — matches the legacy panel.
    cycleRatio(n: any, limit: number): string {
      const v = Number(n)
      return Number.isFinite(v) ? `${v} / ${limit}` : '—'
    },
    num(x: any): number | null {
      const n = Number(x)
      return Number.isFinite(n) ? n : null
    },
    // Per-row provenance chip: card-confirmed / telemetry estimate / mixed.
    sourceLabel(s: string): string {
      return s === 'card' ? this.$t('components.map.panel.tacho.srcCard') as string
        : s === 'partial' ? this.$t('components.map.panel.tacho.srcPartial') as string
          : this.$t('components.map.panel.tacho.srcEstimate') as string
    },
    sourceTitle(s: string): string {
      return s === 'card' ? this.$t('components.map.panel.tacho.srcCardTitle') as string
        : s === 'partial' ? this.$t('components.map.panel.tacho.srcPartialTitle') as string
          : this.$t('components.map.panel.tacho.srcEstimateTitle') as string
    },
    km(x: any): string {
      const n = Number(x)
      return Number.isFinite(n) ? groupThousands(Math.round(n)) + ' km' : '—'
    },
    restDuration(item: any): string {
      return item && item.duration_min != null ? this.hm(item.duration_min) : '—'
    },
    restSpan(item: any): string {
      if (!item || !item.start || !item.end) return '—'
      return formatDateTime(item.start) + ' – ' + formatDateTime(item.end)
    },
    restKind(item: any): string {
      if (!item) return '—'
      return item.label || item.kind || '—'
    },
    routeDuration(): string {
      const pts = this.routePoints
      if (!pts.length) return '—'
      const ts = (p: any) => Number(p?.device_timestamp ?? p?.dt ?? p?.timestamp)
      const first = ts(pts[0])
      const last = ts(pts[pts.length - 1])
      if (!Number.isFinite(first) || !Number.isFinite(last) || last < first) return '—'
      return hoursMinutes(Math.round((last - first) / 60))
    },
    avgConsumption(s: any): string {
      const rate = s.fuel_rate_can ?? s.fuel_rate_map
      const n = Number(rate)
      return Number.isFinite(n) && n > 0 ? n.toFixed(1) + ' L/100km' : '—'
    },
    rangeLabel(): string {
      const [from, to] = this.rangeDates()
      return from && to ? from + ' – ' + to : '—'
    },
    rangeDates(): [string, string] {
      const fmt = 'YYYY-MM-DD HH:mm'
      const now = dayjs()
      if (this.trackRange === 'custom' && this.customFrom && this.customTo) {
        return [dayjs(this.customFrom).format(fmt), dayjs(this.customTo).format(fmt)]
      }
      if (this.trackRange === 'week') {
        return [now.subtract(7, 'day').format(fmt), now.format(fmt)]
      }
      return [now.startOf('day').format(fmt), now.format(fmt)]
    },
    setRange(r: 'day' | 'week' | 'custom'): void {
      if (r === 'custom') {
        if (!this.customFrom || !this.customTo) {
          const now = dayjs()
          this.customFrom = now.startOf('day').format('YYYY-MM-DDTHH:mm')
          this.customTo = now.format('YYYY-MM-DDTHH:mm')
        }
        this.rangeError = ''
        this.showCustomPicker = true
        return
      }
      this.showCustomPicker = false
      if (this.trackRange === r) return
      this.trackRange = r
      this.loadRoute(false)
    },
    applyCustomRange(): void {
      if (!this.customFrom || !this.customTo) {
        this.rangeError = this.$t('components.map.panel.trasa.rangeErrorRequired') as string
        return
      }
      const from = dayjs(this.customFrom)
      const to = dayjs(this.customTo)
      if (!from.isValid() || !to.isValid() || !to.isAfter(from)) {
        this.rangeError = this.$t('components.map.panel.trasa.rangeErrorOrder') as string
        return
      }
      this.rangeError = ''
      this.trackRange = 'custom'
      this.showCustomPicker = false
      this.loadRoute(false)
    },
    cancelCustom(): void {
      this.showCustomPicker = false
      this.rangeError = ''
    },
    extractCurrentCard(points: Array<any>): string | null {
      for (let i = points.length - 1; i >= 0; i--) {
        const raw = points[i]?.driver_card1
        if (raw === undefined || raw === null) continue
        const card = String(raw).trim()
        if (card && !CARD_PLACEHOLDERS.has(card.toUpperCase())) return card
      }
      return null
    },
    // Current driver card: prefer the live card from the last_position payload (always fresh);
    // fall back to the last card seen on the route when the payload does not carry one.
    resolveDriverCard(): string | null {
      const raw = (this.vehicle as any)?.driver_card1
      const card = raw !== undefined && raw !== null ? String(raw).trim() : ''
      if (card && !CARD_PLACEHOLDERS.has(card.toUpperCase())) return card
      return this.extractCurrentCard(this.routePoints)
    },
    async loadRoute(resolveDriver: boolean): Promise<void> {
      const imei = (this.vehicle as any)?.imei
      if (!imei) return
      const [from, to] = this.rangeDates()
      this.loadingRoute = true
      try {
        const [routeRes, sumRes] = await Promise.allSettled([
          getVehicleRoute(imei, from, to),
          getHistorySummary(imei, from, to)
        ])
        // The route endpoint wraps points under `route_data`; tolerate a bare array too.
        const routeData = routeRes.status === 'fulfilled'
          ? (routeRes.value?.data?.route_data ?? routeRes.value?.data)
          : null
        this.routePoints = Array.isArray(routeData) ? routeData : []
        this.summary = sumRes.status === 'fulfilled' ? (sumRes.value?.data ?? null) : null
        // Draw the travelled track (polyline + point tooltips + stop markers) on the map.
        ;(this as any).emitter?.emit('renderVehicleTrack', this.routePoints)
      } finally {
        this.loadingRoute = false
      }
      // Resolve the driver / tacho contract only for vehicles that actually have a tachograph.
      if (resolveDriver && this.hasTacho) {
        this.currentCard = this.resolveDriverCard()
        await this.loadTacho()
      }
    },
    async loadTacho(): Promise<void> {
      this.contract = null
      if (!this.currentCard) return
      const map = await loadCardDriverMap()
      const entry = map[String(this.currentCard)]
      if (!entry) return
      if (entry.name) this.driverName = entry.name
      this.loadingTacho = true
      try {
        const res: any = await getTachoActivityDriver(entry.id)
        this.contract = res?.data ?? null
        const holder = this.contract?.summary?.holder_name
        if (holder) this.driverName = holder
      } catch (_) {
        this.contract = null
      } finally {
        this.loadingTacho = false
      }
    }
  }
})
</script>

<style lang="scss" src="@/assets/scss/mapv3-panel.scss"></style>
